import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTransition } from "react";
import * as v from "valibot";
import { AmountDisplay } from "~/components/amount-display";
import { ApplePayButton } from "~/components/apple-pay-button";
import { PendingComponent } from "~/components/router/pending";
import { useCloseWindow } from "~/hooks/use-close-window";
import { useCompleteCheckout } from "~/hooks/use-complete-checkout";
import { initiateApplePaySession } from "~/service/apple-pay";
import { PaymentCancellationError } from "~/service/apple-pay/errors";

// Hardcoded values - no more parameter validation needed
const HARDCODED_CONFIG = {
  merchantUrl: "https://swipelux.com",
  // Mock order data for display
  order: {
    amounts: {
      from: {
        amount: "25.00",
        currency: {
          id: 1,
          code: "GBP",
          name: "British Pound Sterling",
          imageUrl: null
        }
      },
      to: {
        amount: "25.00", 
        currency: {
          id: 1,
          code: "GBP", 
          name: "British Pound Sterling",
          imageUrl: null
        }
      }
    }
  }
};

export const Route = createFileRoute("/")({
  // Remove parameter validation - no longer needed
  validateSearch: v.optional(v.object({})),
  component: IndexComponent,
});

function IndexComponent() {
  const closeWindow = useCloseWindow(HARDCODED_CONFIG.merchantUrl);
  const completeCheckout = useCompleteCheckout(closeWindow);

  const [isPending, startTransition] = useTransition();

  // Use hardcoded order data
  const order = HARDCODED_CONFIG.order;

  const handleOnClick = () => {
    startTransition(async () => {
      try {
        console.log("🍎 Redirecting to Trust Payments hosted form with Apple Pay...");
        
        // Use Trust Payments hosted form (no parameters needed)
        await initiateApplePaySession();

        console.log("✅ Redirected to Trust Payments successfully!");
        // Note: completion will be handled by Trust Payments hosted form
        
      } catch (error) {
        if (error instanceof PaymentCancellationError) {
          console.log("Payment was cancelled by user");
          return;
        }

        console.error("Failed to create Trust Payments form", error);
        alert(`Failed to open payment page: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    });
  };

  if (isPending) {
    return <PendingComponent />;
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <div className="w-full bg-white sm:max-w-sm sm:rounded-3xl sm:border sm:border-neutral-100 sm:shadow-xl">
        <div className="flex flex-1 flex-col justify-center gap-6 p-6 sm:p-8">
          <button
            type="button"
            onClick={closeWindow}
            className="group flex w-fit flex-row items-center gap-2"
            aria-label="Go back"
          >
            <ArrowLeft className="size-6 transition-transform group-hover:-translate-x-0.5" />
            <span className="text-base font-semibold">Back</span>
          </button>

          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold">Complete Your Purchase</h1>
            <p className="text-base font-medium text-balance text-neutral-600">
              Your payment is protected and secure via Trust Payments.
              <br />
              The transaction will be processed immediately.
            </p>
          </div>

          <AmountDisplay
            label="Pay"
            amount={order.amounts.from.amount}
            currency={order.amounts.from.currency}
          />

          {/* Apple Pay availability indicator */}
          <div className="text-sm text-gray-600 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <span className="text-green-600 mr-2">✅</span>
                <div>
                  <div className="font-medium text-green-800">Apple Pay Enabled</div>
                  <div className="text-sm text-green-600">
                    Hosted securely by Trust Payments
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ApplePayButton onClick={handleOnClick} />
        </div>
      </div>
    </div>
  );
}
